import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { ChevronRight, PlayCircle } from 'lucide-react';
import styled from 'styled-components';

import { getMyTrainings } from '../../../services/trainingService';
import {
  Card,
  CardHeader,
  CardTitle,
  EmptyState,
  ScrollList,
  ListItem,
  ListItemContent,
  ListItemName,
  ListItemSub,
} from '../styles';

const HeaderLink = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }) => theme.colors.primary.main};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
`;

const IconCircle = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #F59E0B, #F97316);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #fff;
`;

const ProgressBar = styled.div`
  position: relative;
  width: 100%;
  height: 4px;
  background: #e2e8f0;
  border-radius: 2px;
  overflow: hidden;
  margin-top: 4px;
`;

const ProgressFill = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: ${({ $pct }) => $pct || 0}%;
  background: #0291EF;
  transition: width 0.3s;
`;

/**
 * "Continue learning" card. Shows the first 5 in-progress assignments so the
 * learner can jump straight back in without navigating through a list page.
 * Hidden when the user has no in-progress assignments.
 */
export default function ContinueLearningCard() {
  const { t } = useTranslation();
  const history = useHistory();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    getMyTrainings({ 'status[eq]': 'in_progress', pageNumber: 1, pageSize: 5 })
      .then((res) => {
        const result = res.data?.data;
        setItems(result?.data || []);
        setTotal(result?.totalRowCount || 0);
      })
      .catch(() => {});
  }, []);

  if (total === 0 && items.length === 0) return null;

  return (
    <Card $delay="0.1s">
      <CardHeader>
        <CardTitle>
          {t('dashboard.continueLearning')}
          {total > 0 ? ` (${total})` : ''}
        </CardTitle>
        <HeaderLink onClick={() => history.push('/my-trainings')}>
          {t('common.seeAll')}
          <ChevronRight size={14} />
        </HeaderLink>
      </CardHeader>
      {items.length === 0 ? (
        <EmptyState>
          <PlayCircle size={28} />
          {t('dashboard.noContinueLearning')}
        </EmptyState>
      ) : (
        <ScrollList>
          {items.map((item) => (
            <ListItem
              key={item.assignmentId}
              style={{ cursor: 'pointer' }}
              onClick={() => history.push(`/my-trainings/${item.assignmentId}`)}
            >
              <IconCircle><PlayCircle size={18} /></IconCircle>
              <ListItemContent>
                <ListItemName>{item.trainingTitle}</ListItemName>
                <ListItemSub>
                  {t('dashboard.progressPercent', { n: item.progressPercentage ?? 0 })}
                </ListItemSub>
                <ProgressBar><ProgressFill $pct={item.progressPercentage ?? 0} /></ProgressBar>
              </ListItemContent>
            </ListItem>
          ))}
        </ScrollList>
      )}
    </Card>
  );
}
